import React, { useEffect, useMemo, useState } from "react";
import RateMeCommentBlock from "./RateMeCommentBlock";
import RateMeReplyBlock from "./RateMeReplyBlock";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isRateMe(comment) {
  return (
    comment?.kind === "rate_me" ||
    comment?.type === "rate_me" ||
    Boolean(comment?.audio?.audio_url)
  );
}

function isRateMeReply(comment) {
  return (
    comment?.kind === "rate_me_reply" ||
    comment?.type === "rate_me_reply" ||
    Boolean(comment?.ratings?.length || comment?.sentenceRating)
  );
}

function getCommentId(comment) {
  return comment?.id || comment?._id;
}

function getAuthorName(comment) {
  return comment?.username || comment?.user || comment?.author || "User";
}

function isAudioLikeFile(value) {
  return /\.(mp3|wav|m4a|webm|ogg|aac)$/i.test(String(value || ""));
}

function getChapterDisplay(comment) {
  if (comment?.chapterTitle) return comment.chapterTitle;

  if (comment?.file && !isAudioLikeFile(comment.file)) {
    return comment.file;
  }

  return "";
}

function getSubjectTitle(comment) {
  return comment?.subject?.title || "Untitled";
}

function getSubjectAuthor(comment) {
  return comment?.subject?.author || "Unknown author";
}

function getSubjectLanguage(comment) {
  return comment?.subject?.language || "Unknown language";
}

function getKindLabel(comment) {
  if (isRateMe(comment)) return "Rate me";
  if (isRateMeReply(comment)) return "Verdict";
  return "Discussion";
}

function getKindStyle(comment) {
  if (isRateMe(comment)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (isRateMeReply(comment)) {
    return "border-green-200 bg-green-50 text-green-700";
  }

  return "border-blue-200 bg-blue-50 text-blue-700";
}

function getCommentDate(comment) {
  const raw =
    comment?.updatedAt ||
    comment?.updated_at ||
    comment?.createdAt ||
    comment?.created_at;

  if (!raw) return null;

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(rawDate) {
  if (!rawDate) return "";

  const date = rawDate instanceof Date ? rawDate : new Date(rawDate);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getLatestComment(comments = []) {
  let latest = null;

  const walk = (items) => {
    for (const item of items || []) {
      const date = getCommentDate(item);

      if (date && (!latest || date > latest.date)) {
        latest = { comment: item, date };
      }

      if (item?.replies?.length) {
        walk(item.replies);
      }
    }
  };

  walk(comments);

  return latest;
}

function getSnippet(comment) {
  const text =
    comment?.content ||
    comment?.text ||
    comment?.comment ||
    comment?.anchor?.text ||
    "";

  const cleaned = String(text).trim();

  if (!cleaned || cleaned === "Human pronunciation verdict") {
    if (isRateMe(comment)) return "Asked for a human pronunciation assessment.";
    if (isRateMeReply(comment)) return "Submitted a human pronunciation verdict.";
    return "No note added.";
  }

  return cleaned.length > 110 ? `${cleaned.slice(0, 110)}…` : cleaned;
}

function countNestedThreads(comments = []) {
  let replies = 0;
  let rateMe = 0;
  let verdicts = 0;
  let discussions = 0;

  const walkReplies = (items) => {
    for (const item of items || []) {
      if (isRateMe(item)) rateMe += 1;
      else if (isRateMeReply(item)) verdicts += 1;
      else discussions += 1;

      if (item?.replies?.length) {
        replies += item.replies.length;
        walkReplies(item.replies);
      }
    }
  };

  walkReplies(comments);

  return {
    threads: comments.length,
    replies,
    rateMe,
    verdicts,
    discussions,
    total: comments.length + replies,
  };
}

function groupComments(comments) {
  const grouped = {};

  for (const comment of comments || []) {
    const language = getSubjectLanguage(comment);
    const subjectTitle = getSubjectTitle(comment);

    if (!grouped[language]) grouped[language] = {};
    if (!grouped[language][subjectTitle]) grouped[language][subjectTitle] = [];

    grouped[language][subjectTitle].push(comment);
  }

  return grouped;
}

function SelectFilter({ label, value, onChange, options, allLabel = "All" }) {
  return (
    <label className="flex min-w-[160px] flex-1 flex-col gap-1 sm:flex-none">
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
      >
        <option value="all">{allLabel}</option>

        {(options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pagination({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;

  return (
    <div className="mt-8 flex items-center justify-center gap-3">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className={cx(
          "rounded-2xl border px-5 py-2.5 text-sm font-black shadow-sm transition",
          page <= 1
            ? "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50"
        )}
      >
        Previous
      </button>

      <span className="rounded-full bg-white px-4 py-2 text-sm font-black text-slate-500 shadow-sm">
        {page} / {totalPages}
      </span>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        className={cx(
          "rounded-2xl border px-5 py-2.5 text-sm font-black shadow-sm transition",
          page >= totalPages
            ? "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50"
        )}
      >
        Next
      </button>
    </div>
  );
}

function Pill({ children, className }) {
  if (!children) return null;

  return (
    <span
      className={cx(
        "inline-flex max-w-[220px] items-center truncate rounded-full border px-2.5 py-1 text-[11px] font-black",
        className || "border-slate-200 bg-slate-50 text-slate-500"
      )}
    >
      {children}
    </span>
  );
}

function MetricPill({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-600",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    green: "border-green-200 bg-green-50 text-green-700",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-black",
        tones[tone] || tones.slate
      )}
    >
      <span>{value}</span>
      <span className="font-bold opacity-75">{label}</span>
    </span>
  );
}

function Chevron({ open }) {
  return (
    <span
      className={cx(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition",
        open ? "rotate-90" : "rotate-0"
      )}
    >
      ›
    </span>
  );
}

function CommentMeta({ comment }) {
  const chapterDisplay = getChapterDisplay(comment);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Pill>{getSubjectLanguage(comment)}</Pill>
      <Pill>{getSubjectAuthor(comment)}</Pill>
      <Pill>{chapterDisplay}</Pill>
      <Pill className={getKindStyle(comment)}>{getKindLabel(comment)}</Pill>
    </div>
  );
}

function SimpleCommentBody({ comment }) {
  const content = comment?.content || comment?.text || comment?.comment || "";

  if (comment?.isDeleted || comment?.deleted) {
    return (
      <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-bold italic text-slate-400">
        Deleted comment
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <p className="whitespace-pre-wrap break-words text-[15px] font-medium leading-7 text-slate-900">
        {content || "No content"}
      </p>
    </div>
  );
}

function ThreadHeader({ comment }) {
  const name = getAuthorName(comment);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-black text-white shadow-md shadow-blue-600/20">
            {String(name).charAt(0).toUpperCase()}
          </div>

          <div>
            <h3 className="truncate text-lg font-black text-slate-950">
              {name}
            </h3>

            <div className="text-xs font-bold text-slate-400">
              {formatDate(comment?.createdAt || comment?.created_at)}
            </div>
          </div>
        </div>

        <CommentMeta comment={comment} />
      </div>

      <div className="rounded-full bg-slate-50 px-3 py-1 text-xs font-black text-slate-500">
        Score {comment?.score || 0}
      </div>
    </div>
  );
}

function GlobalCommentCard({ comment, currentUsername, onRefresh, depth = 0 }) {
  const replies = comment?.replies || [];

 const alreadyAnsweredByMe =
  isRateMe(comment) && Boolean(comment?.myRateMeReply);

  const payloadBase = {
    subject: comment?.subject,
    file: comment?.file,
    chapterTitle:
      getChapterDisplay(comment) || comment?.chapterTitle || comment?.file,
  };

  const anchor = comment?.anchor?.text
    ? {
        text: comment.anchor.text,
        start: comment.anchor.start,
        end: comment.anchor.end,
      }
    : null;

  return (
    <article
      className={cx(
        "rounded-[28px] border bg-white p-5 shadow-sm transition hover:shadow-md",
        depth > 0
          ? "border-slate-200"
          : isRateMe(comment)
          ? "border-violet-200"
          : "border-slate-200"
      )}
    >
      <ThreadHeader comment={comment} />

      {comment?.anchor?.text && !isRateMe(comment) && !isRateMeReply(comment) && (
        <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3">
          <div className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-blue-500">
            Referenced text
          </div>

          <div className="line-clamp-2 text-sm italic leading-6 text-slate-600">
            “{comment.anchor.text}”
          </div>
        </div>
      )}

      {isRateMe(comment) && !comment?.isDeleted ? (
        <RateMeCommentBlock
          comment={comment}
          content={comment?.content || ""}
          canInteract={Boolean(localStorage.getItem("token"))}
          payloadBase={payloadBase}
          anchor={anchor}
          onSubmitted={onRefresh}
          alreadyAnswered={alreadyAnsweredByMe}
        />
      ) : isRateMeReply(comment) && !comment?.isDeleted ? (
        <RateMeReplyBlock reply={comment} />
      ) : (
        <SimpleCommentBody comment={comment} />
      )}

      {replies.length > 0 && (
        <div className="mt-5 space-y-3 border-l-2 border-slate-100 pl-4">
          {replies.map((reply, index) => (
            <GlobalCommentCard
              key={getCommentId(reply) || index}
              comment={reply}
              currentUsername={currentUsername}
              onRefresh={onRefresh}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </article>
  );
}

function SubjectPreview({ comments }) {
  const stats = countNestedThreads(comments);
  const latest = getLatestComment(comments);
  const latestComment = latest?.comment;

  return (
    <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
      <div className="min-w-0 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <MetricPill label="threads" value={stats.threads} tone="blue" />

          {stats.rateMe > 0 && (
            <MetricPill label="Rate me" value={stats.rateMe} tone="violet" />
          )}

          {stats.verdicts > 0 && (
            <MetricPill label="verdicts" value={stats.verdicts} tone="green" />
          )}

          {stats.replies > 0 && (
            <MetricPill label="replies" value={stats.replies} />
          )}
        </div>

        {latestComment && (
          <div className="mt-3 min-w-0 text-sm">
            <span className="font-black text-slate-700">
              Latest: {getAuthorName(latestComment)}
            </span>
            <span className="mx-2 text-slate-300">·</span>
            <span className="font-bold text-slate-400">
              {formatDate(latest?.date)}
            </span>

            <div className="mt-1 line-clamp-1 font-semibold text-slate-500">
              {getSnippet(latestComment)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SubjectSection({
  subjectName,
  comments,
  onRefresh,
  currentUsername,
  forceOpenToken,
  forceClosedToken,
}) {
  const [open, setOpen] = useState(false);
  const first = comments?.[0];

  useEffect(() => {
    if (forceOpenToken > 0) setOpen(true);
  }, [forceOpenToken]);

  useEffect(() => {
    if (forceClosedToken > 0) setOpen(false);
  }, [forceClosedToken]);

  useEffect(() => {
    setOpen(false);
  }, [subjectName]);

  return (
    <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-start justify-between gap-4 p-4 text-left transition hover:bg-slate-50/70 sm:p-5"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-xl font-black text-slate-950">
              {subjectName}
            </h3>

            <Pill>{getSubjectAuthor(first)}</Pill>
            <Pill>{getSubjectLanguage(first)}</Pill>
          </div>

          <SubjectPreview comments={comments} />
        </div>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/40 p-4 sm:p-5">
          <div className="space-y-4">
            {comments.map((comment, index) => (
              <GlobalCommentCard
                key={getCommentId(comment) || index}
                comment={comment}
                currentUsername={currentUsername}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function LanguagePreview({ subjects }) {
  const allComments = Object.values(subjects).flat();
  const stats = countNestedThreads(allComments);
  const latest = getLatestComment(allComments);

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <MetricPill label="subjects" value={Object.keys(subjects).length} />
      <MetricPill label="threads" value={stats.threads} tone="blue" />

      {stats.rateMe > 0 && (
        <MetricPill label="Rate me" value={stats.rateMe} tone="violet" />
      )}

      {stats.verdicts > 0 && (
        <MetricPill label="verdicts" value={stats.verdicts} tone="green" />
      )}

      {latest?.comment && (
        <span className="inline-flex min-w-0 max-w-full rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-500">
          <span className="shrink-0 text-slate-400">Latest&nbsp;</span>
          <span className="truncate">
            {getAuthorName(latest.comment)} · {formatDate(latest.date)}
          </span>
        </span>
      )}
    </div>
  );
}

function LanguageSection({
  languageName,
  subjects,
  onRefresh,
  currentUsername,
  forceOpenToken,
  forceClosedToken,
}) {
  const [open, setOpen] = useState(false);

  const subjectEntries = Object.entries(subjects).sort(
    ([, commentsA], [, commentsB]) => {
      const latestA = getLatestComment(commentsA)?.date?.getTime?.() || 0;
      const latestB = getLatestComment(commentsB)?.date?.getTime?.() || 0;

      return latestB - latestA;
    }
  );

  useEffect(() => {
    if (forceOpenToken > 0) setOpen(true);
  }, [forceOpenToken]);

  useEffect(() => {
    if (forceClosedToken > 0) setOpen(false);
  }, [forceClosedToken]);

  useEffect(() => {
    setOpen(false);
  }, [languageName]);

  return (
    <section className="overflow-hidden rounded-[36px] border border-slate-200 bg-white/80 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-start justify-between gap-4 bg-gradient-to-br from-white via-slate-50 to-blue-50/40 p-5 text-left transition hover:from-slate-50 hover:to-blue-50 sm:p-6"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
            Language
          </div>

          <h2 className="mt-1 text-2xl font-black text-slate-950">
            {languageName}
          </h2>

          <LanguagePreview subjects={subjects} />
        </div>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="space-y-4 border-t border-slate-100 p-4 sm:p-5">
          {subjectEntries.map(([subjectName, comments]) => (
            <SubjectSection
              key={subjectName}
              subjectName={subjectName}
              comments={comments}
              onRefresh={onRefresh}
              currentUsername={currentUsername}
              forceOpenToken={forceOpenToken}
              forceClosedToken={forceClosedToken}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function CommunityCommentsBrowser({ auth, successHandler }) {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({
    languages: [],
    authors: [],
    subjects: [],
    kinds: [],
  });

  const [language, setLanguage] = useState("all");
  const [author, setAuthor] = useState("all");
  const [subjectTitle, setSubjectTitle] = useState("all");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("active");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [pagination, setPagination] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [forceOpenToken, setForceOpenToken] = useState(0);
  const [forceClosedToken, setForceClosedToken] = useState(0);

  const currentUsername =
    auth?.username ||
    auth?.user?.username ||
    auth?.currentUser?.username ||
    localStorage.getItem("username") ||
    null;

    
  const grouped = useMemo(() => groupComments(items), [items]);

  const languageEntries = useMemo(() => {
    return Object.entries(grouped).sort(([, subjectsA], [, subjectsB]) => {
      const latestA =
        getLatestComment(Object.values(subjectsA).flat())?.date?.getTime?.() ||
        0;

      const latestB =
        getLatestComment(Object.values(subjectsB).flat())?.date?.getTime?.() ||
        0;

      return latestB - latestA;
    });
  }, [grouped]);

  const resetFilters = () => {
    setLanguage("all");
    setAuthor("all");
    setSubjectTitle("all");
    setKind("all");
    setSort("active");
    setPage(1);
    setForceClosedToken((value) => value + 1);
  };

 const fetchComments = async () => {
  setLoading(true);
  setError("");

  try {
    const token = localStorage.getItem("token");

    const url = `${process.env.REACT_APP_USER_API_URL}/comments/community?page=${page}&pageSize=${pageSize}&sort=${sort}&language=${encodeURIComponent(
      language
    )}&author=${encodeURIComponent(author)}&subjectTitle=${encodeURIComponent(
      subjectTitle
    )}&kind=${encodeURIComponent(kind)}`;

    console.log("[CommunityComments] fetch start", {
      url,
      page,
      pageSize,
      sort,
      language,
      author,
      subjectTitle,
      kind,
      hasToken: Boolean(token),
      tokenPreview: token ? `${token.slice(0, 12)}…` : null,
      auth,
      currentUsername,
    });

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
    });

    console.log("[CommunityComments] fetch response meta", {
      ok: res.ok,
      status: res.status,
      statusText: res.statusText,
      contentType: res.headers.get("content-type"),
    });

    if (!res.ok) {
      const errorText = await res.text().catch(() => "");

      console.error("[CommunityComments] fetch failed body", {
        status: res.status,
        errorText,
      });

      throw new Error(`Community comments ${res.status}`);
    }

    const json = await res.json();
    const nextItems = json.items || [];

    const rateMeRoots = nextItems
      .filter((item) => item.kind === "rate_me" || item.audio?.audio_url)
      .map((item) => ({
        id: item.id,
        kind: item.kind,
        username: item.username,
        userId: item.userId,
        myRateMeReply: item.myRateMeReply,
        replyCount: item.replyCount,
        repliesLength: item.replies?.length || 0,
        replies: (item.replies || []).map((reply) => ({
          id: reply.id,
          kind: reply.kind,
          type: reply.type,
          username: reply.username,
          user: reply.user,
          author: reply.author,
          userId: reply.userId,
          sentenceRating: reply.sentenceRating,
          ratingsLength: reply.ratings?.length || 0,
          isDeleted: reply.isDeleted,
        })),
      }));

    console.log("[CommunityComments] response parsed", {
      page: json.page,
      pageSize: json.pageSize,
      totalThreads: json.totalThreads,
      totalPages: json.totalPages,
      sort: json.sort,
      facets: json.facets,
      itemsLength: nextItems.length,
      firstItem: nextItems[0],
      rateMeRoots,
    });

    console.table(
      rateMeRoots.flatMap((root) =>
        root.replies.length
          ? root.replies.map((reply) => ({
              rootId: root.id,
              rootUser: root.username,
              rootUserId: root.userId,
              rootMyRateMeReply: root.myRateMeReply,
              replyId: reply.id,
              replyKind: reply.kind,
              replyUsername: reply.username,
              replyUserId: reply.userId,
              currentUsername,
              usernameMatches:
                (reply.username || reply.user || reply.author) ===
                currentUsername,
              hasSentenceRating: Boolean(reply.sentenceRating),
              ratingsLength: reply.ratingsLength,
            }))
          : [
              {
                rootId: root.id,
                rootUser: root.username,
                rootUserId: root.userId,
                rootMyRateMeReply: root.myRateMeReply,
                replyId: null,
                replyKind: null,
                replyUsername: null,
                replyUserId: null,
                currentUsername,
                usernameMatches: false,
                hasSentenceRating: false,
                ratingsLength: 0,
              },
            ]
      )
    );

    setItems(nextItems);
    setFacets(json.facets || {});
    setPagination(json);
    setForceClosedToken((value) => value + 1);
  } catch (err) {
    console.error("[CommunityComments] fetch community comments failed", err);
    setError("Could not load community comments.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchComments();
  }, [page, sort, language, author, subjectTitle, kind]);

  useEffect(() => {
    setPage(1);
  }, [sort, language, author, subjectTitle, kind]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 overflow-hidden rounded-[36px] border border-slate-200 bg-white shadow-sm">
          <div className="relative border-b border-slate-100 bg-gradient-to-br from-white via-blue-50/50 to-violet-50/40 p-6 sm:p-8">
            <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-blue-300/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-52 w-52 rounded-full bg-violet-300/20 blur-3xl" />

            <div className="relative flex flex-wrap items-start justify-between gap-5">
              <div>
                <button
                  type="button"
                  onClick={() => successHandler?.(false)}
                  className="mb-5 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
                >
                  <span>←</span>
                  <span>Back to library</span>
                </button>

                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                  Community
                </div>

                <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                  Community comments
                </h1>

                <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
                  Ask for human assessments, share tips, discuss tricky parts,
                  and learn together with fellow learners.
                </p>
              </div>

              <div className="rounded-[28px] bg-slate-950 px-6 py-5 text-white shadow-xl shadow-slate-950/10">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-white/50">
                  Threads
                </div>

                <div className="mt-1 text-4xl font-black">
                  {pagination?.totalThreads ?? items.length}
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              <SelectFilter
                label="Language"
                value={language}
                onChange={setLanguage}
                options={facets.languages}
              />

              <SelectFilter
                label="Author"
                value={author}
                onChange={setAuthor}
                options={facets.authors}
              />

              <SelectFilter
                label="Subject"
                value={subjectTitle}
                onChange={setSubjectTitle}
                options={facets.subjects}
              />

              <SelectFilter
                label="Type"
                value={kind}
                onChange={setKind}
                options={facets.kinds}
              />

              <label className="flex min-w-[160px] flex-1 flex-col gap-1 sm:flex-none">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                  Sort
                </span>

                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-11 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                >
                  <option value="active">Recently active</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="ranking">Top ranked</option>
                </select>
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-bold text-slate-400">
                {loading
                  ? "Loading…"
                  : `${pagination?.totalThreads ?? items.length} thread${
                      (pagination?.totalThreads ?? items.length) === 1
                        ? ""
                        : "s"
                    } found`}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setForceOpenToken((value) => value + 1)}
                  className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-black text-blue-700 shadow-sm transition hover:bg-blue-100"
                >
                  Expand all
                </button>

                <button
                  type="button"
                  onClick={() => setForceClosedToken((value) => value + 1)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Collapse all
                </button>

                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Reset filters
                </button>
              </div>
            </div>
          </div>
        </header>

        {error && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-[32px] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-2xl">
              💬
            </div>
            <div className="text-base font-bold text-slate-500">
              Loading community comments…
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-[32px] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-100 text-2xl">
              🔎
            </div>
            <div className="text-base font-bold text-slate-500">
              No comments found.
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {languageEntries.map(([languageName, subjects]) => (
              <LanguageSection
                key={languageName}
                languageName={languageName}
                subjects={subjects}
                onRefresh={fetchComments}
                currentUsername={currentUsername}
                forceOpenToken={forceOpenToken}
                forceClosedToken={forceClosedToken}
              />
            ))}
          </div>
        )}

        <Pagination
          page={page}
          totalPages={pagination?.totalPages || 1}
          setPage={setPage}
        />
      </div>
    </div>
  );
}